import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader, Tabs, type TabDef } from "@/components/ui";
import { TestersPanel } from "@/components/testers-tabs/testers-panel";
import { CompaniesPanel } from "@/components/testers-tabs/companies-panel";
import { KitsPanel } from "@/components/testers-tabs/kits-panel";
import { InvitesPanel } from "@/components/testers-tabs/invites-panel";
import { RequestsPanel } from "@/components/testers-tabs/requests-panel";

type TestersTab = "testers" | "companies" | "kits" | "invites" | "requests";

const ALLOWED_TABS: TestersTab[] = ["testers", "companies", "kits", "invites", "requests"];

interface TestersHomeSearch {
  tab?: TestersTab;
  /** Testers-tab deep link — scopes the testers list to one company's testers. */
  testing_company_id?: string;
  /** Testers-tab deep link — seeds the synthetic "Locked out" filter (suspended OR revoked). */
  locked_out?: boolean;
}

function parseTab(value: unknown): TestersTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((tab) => tab === value);
}

export const Route = createFileRoute("/_authenticated/testers/")({
  component: TestersHomePage,
  validateSearch: (search: Record<string, unknown>): TestersHomeSearch => ({
    tab: parseTab(search.tab),
    testing_company_id:
      typeof search.testing_company_id === "string" ? search.testing_company_id : undefined,
    locked_out: search.locked_out === true || search.locked_out === "true" ? true : undefined,
  }),
});

/**
 * The consolidated testers home: one tabbed surface over the three former
 * field-operations list pages (testers, testing companies, test kits), the
 * pending-invites management surface promoted to its own tab, and the
 * access-request inbox the utility decides company and tester asks from. Only
 * the active tab's panel mounts; the tab rides the `?tab=` search param
 * against the allow-list above (absent or unknown → testers) and tab switches
 * navigate with `replace: true` so history keeps one entry per page, not per
 * tab. The testers tab's deep links (`testing_company_id`, `locked_out`)
 * ride the same URL and seed that panel's filters.
 */
function TestersHomePage() {
  const { t } = useTranslation("testers");
  const navigate = useNavigate();
  const { tab, testing_company_id: companyIdParam, locked_out: lockedOutParam } = Route.useSearch();

  const activeTab: TestersTab = tab ?? "testers";

  const setActiveTab = (next: TestersTab) => {
    void navigate({
      to: "/testers",
      search: next === "testers" ? {} : { tab: next },
      replace: true,
    });
  };

  const tabs: TabDef<TestersTab>[] = [
    { value: "testers", label: t("page.tabs.testers") },
    { value: "companies", label: t("page.tabs.companies") },
    { value: "kits", label: t("page.tabs.kits") },
    { value: "invites", label: t("page.tabs.invites") },
    { value: "requests", label: t("page.tabs.requests") },
  ];

  return (
    <div className="p-8">
      <PageHeader title={t("list.title")} />

      <div className="mt-6">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("list.title")}
        />

        <div
          className="mt-6"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === "testers" && (
            <TestersPanel testingCompanyId={companyIdParam} lockedOut={lockedOutParam} />
          )}
          {activeTab === "companies" && <CompaniesPanel />}
          {activeTab === "kits" && <KitsPanel />}
          {activeTab === "invites" && <InvitesPanel />}
          {activeTab === "requests" && <RequestsPanel />}
        </div>
      </div>
    </div>
  );
}
